/**
 * @file 光伏管理
 */

import request from '@/utils/request'

/**
 * @typedef {Object} Photovoltaic 光伏对象
 * @property {string|number} [id] 光伏Id
 * @property {string|number} stationId 电站Id
 * @property {string} uuid 设备UUID（唯一标识）
 * @property {string} [devId] 设备ID（设备编码）
 * @property {string} photovoltaicName 光伏名称
 * @property {string} [factoryName] 厂家名称
 * @property {string} [productModel] 产品型号
 * @property {string|number} [parentEquipmentId] 关联设备ID
 * @property {string} [maxDcVoltage] 最大直流电压(V)
 * @property {string} [maxDcPower] 最大直流功率(kW)
 * @property {string} [ratedOutputPower] 额定输出功率(kW)
 * @property {string} [maxOutputPower] 最大输出功率(kW)
 * @property {string} [maxAcOutputCurrent] 最大交流输出电流(A)
 * @property {string} [maxInputCurrent] 最大输入电流(A)
 * @property {string} [ratedGridVoltage] 额定电网电压(V)
 * @property {string} [ratedGridFrequency] 额定电网频率(Hz)
 * @property {number} [sort] 排序号
 */

/**
 * 修改光伏
 * @param {Photovoltaic} pv
 */
export function updatePv(pv) {
  return request({
    url: '/device/v3/photovoltaic',
    method: 'put',
    data: pv
  })
}

/**
 * 新增光伏
 * @param {Photovoltaic} pv
 */
export function addPv(pv) {
  return request({
    url: '/device/v3/photovoltaic',
    method: 'post',
    data: pv
  })
}


/**
 * 获取光伏列表
 * @param {object} params
 * @param {string} params.stationId
 * @param {string} [params.photovoltaicName]
 * @param {string} [params.productModel]
 * @param {string} [params.pageNum]
 * @param {string} [params.pageSize]
 * @returns
 */
export function getPvList(params) {
  return request({
    url: '/device/v3/photovoltaic/list',
    method: 'get',
    params: params
  })
}


/**
 * 批量删除光伏
 * @param {string|number} ids 光伏Id
 * @returns
 */
export function delPvList(ids) {
  if (Array.isArray(ids)) {
    ids = ids.join(',')
  }
  return request({
    url: `/device/v3/photovoltaic/${ids}`,
    method: 'delete',
  })
}


/**
 * 获取光伏详细信息
 * @param {string|number} id 光伏Id
 */
export function getPv(id) {
  return request({
    url: `/device/v3/photovoltaic/${id}`,
    method: 'get',
  })
}

// 光伏实时数据
///////////////////////////////////////////////////////////////////////////////////


/**
 * 光伏设备模型（点表）
 * @param {string|number} stationId 电站Id
 * @returns
 */
export function getPvDataModel(stationId) {
  return request({
    url: `/dashboard/photovoltaic/data/model/${stationId}`,
    method: 'get',
  })
}


/**
 * 光伏设备实时数据
 * @param {string|number} stationId 电站Id
 * @returns
 */
export function getPvDataRealtime(stationId) {
  return request({
    url: `/dashboard/photovoltaic/data/realtime/${stationId}`,
    method: 'get',
  })
}

/**
 * 折线图数据
 * @param {string|number} stationId 电站Id
 * @returns
 */
export function getPvDataStatistics(stationId) {
  return request({
    url: `/dashboard/photovoltaic/data/statistics/${stationId}`,
    method: 'get',
  })
}

/**
 * 光伏每日发电量统计
 * @param {object} params
 * @param {string} params.stationId 电站Id
 * @param {string} params.start 开始时间
 * @param {string} params.end 结束时间
 * @returns
 */
export function getPvDataDailyPowerGeneration(params) {
  return request({
    url: `/dashboard/photovoltaic/data/daily-power-generation`,
    method: 'get',
    params: {
      stationId: params.stationId,
      startTime: params.start,
      endTime: params.end,
    }
  })
}

/**
 * 光伏设备总览数据
 * @param {string|number} stationId 电站ID
 * @returns
 */
export function getPvDataOverview(stationId) {
  return request({
    url: `/dashboard/photovoltaic/data/overview/${stationId}`,
    method: 'get',
  })
}

/**
 * 光伏设备详情数据
 * @param {number} stationId 电站ID
 * @param {string} uuid 设备UUID
 * @returns
 */
export function getPvDataDetailRealtime(stationId, uuid) {
  return request({
    url: `/dashboard/photovoltaic/data/realtime/${stationId}/${uuid}`,
    method: 'get',
  })
}
